'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, UserPlus, Building2, Search } from 'lucide-react';

interface SignupFormData {
  role: 'owner' | 'seeker';
  fullName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
}

interface Props {
  onSwitchToLogin: () => void;
}

export default function SignupForm({ onSwitchToLogin }: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState<'owner' | 'seeker'>('seeker');

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<SignupFormData>({
    defaultValues: { role: 'seeker' },
  });

  const password = watch('password');

  const onSubmit = async (data: SignupFormData) => {
    setLoading(true);
    // TODO: connect to /api/auth/register endpoint
    await new Promise(r => setTimeout(r, 1400));
    setLoading(false);
    toast.success('تم إنشاء حسابك بنجاح! مرحباً بك في عقار ماركت');
    setTimeout(() => {
      if (data.role === 'owner') {
        window.location.href = '/property-management';
      } else {
        window.location.href = '/property-listings';
      }
    }, 800);
  };

  const handleRoleSelect = (role: 'owner' | 'seeker') => {
    setSelectedRole(role);
    setValue('role', role);
  };

  return (
    <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-6">
      <h2 className="text-xl font-700 text-gray-900 mb-1">إنشاء حساب جديد</h2>
      <p className="text-sm text-gray-500 mb-5">انضم مجاناً واستفد من جميع المزايا</p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Role Selector */}
        <div>
          <label className="block text-sm font-600 text-gray-700 mb-2">أنا...</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => handleRoleSelect('seeker')}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all duration-150 ${
                selectedRole === 'seeker' ?'border-primary-500 bg-primary-50' :'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <Search size={22} className={selectedRole === 'seeker' ? 'text-primary-500' : 'text-gray-400'} />
              <div>
                <div className={`text-sm font-700 ${selectedRole === 'seeker' ? 'text-primary-600' : 'text-gray-700'}`}>باحث عن عقار</div>
                <div className="text-xs text-gray-400">أبحث عن شراء أو إيجار</div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => handleRoleSelect('owner')}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all duration-150 ${
                selectedRole === 'owner' ?'border-primary-500 bg-primary-50' :'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <Building2 size={22} className={selectedRole === 'owner' ? 'text-primary-500' : 'text-gray-400'} />
              <div>
                <div className={`text-sm font-700 ${selectedRole === 'owner' ? 'text-primary-600' : 'text-gray-700'}`}>مالك عقار</div>
                <div className="text-xs text-gray-400">أريد إدراج عقاراتي</div>
              </div>
            </button>
          </div>
          <input type="hidden" {...register('role')} />
        </div>

        {/* Full Name */}
        <div>
          <label className="block text-sm font-600 text-gray-700 mb-1">الاسم الكامل</label>
          <input
            type="text"
            placeholder="أحمد محمد العتيبي"
            {...register('fullName', { required: 'الاسم مطلوب', minLength: { value: 3, message: 'الاسم يجب أن يكون 3 أحرف على الأقل' } })}
            className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 transition-colors text-right ${errors.fullName ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
          />
          {errors.fullName && <p className="text-xs text-red-500 mt-1">{errors.fullName.message}</p>}
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-600 text-gray-700 mb-1">البريد الإلكتروني</label>
          <input
            type="email"
            placeholder="example@email.com"
            {...register('email', {
              required: 'البريد الإلكتروني مطلوب',
              pattern: { value: /^\S+@\S+\.\S+$/, message: 'صيغة البريد غير صحيحة' },
            })}
            className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 transition-colors text-right ${errors.email ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
          />
          {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>}
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-600 text-gray-700 mb-1">رقم الجوال</label>
          <p className="text-xs text-gray-400 mb-1">سيُستخدم للتحقق من هويتك وللتواصل</p>
          <input
            type="tel"
            placeholder="05XXXXXXXX"
            {...register('phone', {
              required: 'رقم الجوال مطلوب',
              pattern: { value: /^05\d{8}$/, message: 'رقم الجوال يجب أن يبدأ بـ 05 ويتكون من 10 أرقام' },
            })}
            className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 transition-colors text-right tabular-nums ${errors.phone ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
          />
          {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone.message}</p>}
        </div>

        {/* Password */}
        <div>
          <label className="block text-sm font-600 text-gray-700 mb-1">كلمة المرور</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="8 أحرف على الأقل"
              {...register('password', {
                required: 'كلمة المرور مطلوبة',
                minLength: { value: 8, message: 'كلمة المرور يجب أن تكون 8 أحرف على الأقل' },
                pattern: { value: /(?=.*[A-Z])(?=.*\d)/, message: 'يجب أن تحتوي على حرف كبير ورقم' },
              })}
              className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 transition-colors text-right pl-10 ${errors.password ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password.message}</p>}
        </div>

        {/* Confirm Password */}
        <div>
          <label className="block text-sm font-600 text-gray-700 mb-1">تأكيد كلمة المرور</label>
          <div className="relative">
            <input
              type={showConfirm ? 'text' : 'password'}
              placeholder="أعد كتابة كلمة المرور"
              {...register('confirmPassword', {
                required: 'تأكيد كلمة المرور مطلوب',
                validate: val => val === password || 'كلمتا المرور غير متطابقتين',
              })}
              className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 transition-colors text-right pl-10 ${errors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
            />
            <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.confirmPassword && <p className="text-xs text-red-500 mt-1">{errors.confirmPassword.message}</p>}
        </div>

        {/* Terms */}
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            {...register('terms', { required: 'يجب الموافقة على الشروط والأحكام' })}
            className="w-4 h-4 rounded border-gray-300 text-primary-500 focus:ring-primary-300 mt-0.5"
          />
          <span className="text-xs text-gray-600">
            أوافق على{' '}
            <a href="#" className="text-primary-500 hover:underline">شروط الاستخدام</a>
            {' '}و{' '}
            <a href="#" className="text-primary-500 hover:underline">سياسة الخصوصية</a>
          </span>
        </label>
        {errors.terms && <p className="text-xs text-red-500 -mt-2">{errors.terms.message}</p>}

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-600 rounded-xl transition-all duration-150 active:scale-95 flex items-center justify-center gap-2"
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              <UserPlus size={16} />
              إنشاء الحساب مجاناً
            </>
          )}
        </button>
      </form>

      <div className="mt-4 text-center text-sm text-gray-500">
        لديك حساب بالفعل؟{' '}
        <button onClick={onSwitchToLogin} className="text-primary-500 font-600 hover:text-primary-700 transition-colors">
          سجّل دخولك
        </button>
      </div>
    </div>
  );
}